import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { PracticePage } from './pages/PracticePage';
import { DashboardPage } from './pages/DashboardPage';
import { MasteryGatePage } from './pages/MasteryGatePage';
import { LessonSelectPage } from './pages/LessonSelectPage';
import { TimedChallengePage } from './pages/TimedChallengePage';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { AddGradePage } from './pages/AddGradePage';
import { VerifyEmailPage } from './pages/VerifyEmailPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ParentDashboardPage } from './pages/ParentDashboardPage';
import { AdminDashboardPage } from './pages/AdminDashboardPage';
import { StudentSettingsPage } from './pages/StudentSettingsPage';
import { SupportPage } from './pages/SupportPage';
import { LinkRequestBanner } from './components/student/LinkRequestBanner';
import { respondToLinkRequest } from './services/auth';
import './styles/App.css';

const AppHeader: React.FC = () => {
  const { user, logout } = useAuth();

  if (!user) {
    return null;
  }

  const handleLogout = async () => {
    await logout();
  };

  return (
    <header className="app-header">
      <Link to="/" className="app-logo">
        <svg className="app-logo-icon" viewBox="0 0 64 64" fill="none">
          <rect width="64" height="64" rx="16" fill="#2563EB"/>
          <rect x="12" y="22" width="10" height="20" rx="3" fill="#FACC15"/>
          <rect x="42" y="22" width="10" height="20" rx="3" fill="#FACC15"/>
          <rect x="22" y="28" width="20" height="8" rx="2" fill="white"/>
          <line x1="17" y1="29" x2="17" y2="35" stroke="#1E40AF" strokeWidth="2.5" strokeLinecap="round"/>
          <line x1="14" y1="32" x2="20" y2="32" stroke="#1E40AF" strokeWidth="2.5" strokeLinecap="round"/>
          <line x1="44.5" y1="29.5" x2="49.5" y2="34.5" stroke="#1E40AF" strokeWidth="2.5" strokeLinecap="round"/>
          <line x1="49.5" y1="29.5" x2="44.5" y2="34.5" stroke="#1E40AF" strokeWidth="2.5" strokeLinecap="round"/>
          <circle cx="52" cy="16" r="2.5" fill="#FACC15"/>
        </svg>
        <span className="app-logo-text">Reps<span>Learn</span></span>
      </Link>
      <nav className={user.role === 'student' ? 'student-nav' : ''}>
        {user.role === 'student' ? (
          <>
            <Link to="/dashboard" className="nav-tab">
              <svg className="nav-tab-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="7" rx="1"/>
                <rect x="14" y="3" width="7" height="7" rx="1"/>
                <rect x="3" y="14" width="7" height="7" rx="1"/>
                <rect x="14" y="14" width="7" height="7" rx="1"/>
              </svg>
              <span className="nav-tab-label">Dashboard</span>
            </Link>
            <Link to="/practice" className="nav-tab">
              <svg className="nav-tab-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
                <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
              </svg>
              <span className="nav-tab-label">Practice</span>
            </Link>
            <Link to="/challenge" className="nav-tab">
              <svg className="nav-tab-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="8" r="6"/>
                <path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11"/>
              </svg>
              <span className="nav-tab-label">Exams</span>
            </Link>
            <Link to="/settings" className="nav-tab">
              <svg className="nav-tab-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/>
                <circle cx="12" cy="12" r="3"/>
              </svg>
              <span className="nav-tab-label">Settings</span>
            </Link>
          </>
        ) : (
          <Link to="/parent/dashboard">Parent Dashboard</Link>
        )}
        {user.isAdmin && <Link to="/admin">Admin</Link>}
      </nav>
      <div className="user-menu">
        <span className="user-name">{user.name}</span>
        <button onClick={handleLogout} className="logout-button">
          Logout
        </button>
      </div>
    </header>
  );
};

const AppContent: React.FC = () => {
  const { user, loading, pendingLinkRequests, refreshLinkRequests } = useAuth();

  const handleLinkResponse = async (requestId: string, action: 'accept' | 'decline'): Promise<string> => {
    const result = await respondToLinkRequest(requestId, action);
    await refreshLinkRequests();
    return result.message;
  };

  if (loading) {
    return (
      <div className="app-loading">
        <div className="loading-spinner" />
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="app">
      <AppHeader />

      {user?.role === 'student' && pendingLinkRequests.length > 0 && (
        <LinkRequestBanner requests={pendingLinkRequests} onRespond={handleLinkResponse} />
      )}

      <main className="app-main">
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={
            user ? <Navigate to={user.role === 'parent' ? '/parent/dashboard' : '/dashboard'} /> : <LoginPage />
          } />
          <Route path="/register" element={
            user ? <Navigate to={user.role === 'parent' ? '/parent/dashboard' : '/dashboard'} /> : <RegisterPage />
          } />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/support" element={<SupportPage />} />

          {/* Email verification (accessible when logged in but not verified) */}
          <Route path="/verify-email" element={
            <ProtectedRoute requireVerified={false}>
              <VerifyEmailPage />
            </ProtectedRoute>
          } />

          {/* Protected student routes */}
          <Route path="/" element={
            <ProtectedRoute>
              {user?.role === 'parent' ? <Navigate to="/parent/dashboard" /> : <DashboardPage />}
            </ProtectedRoute>
          } />
          <Route path="/dashboard" element={
            <ProtectedRoute requireRole="student">
              <DashboardPage />
            </ProtectedRoute>
          } />
          <Route path="/practice" element={
            <ProtectedRoute requireRole="student">
              <LessonSelectPage />
            </ProtectedRoute>
          } />
          <Route path="/practice/:unit" element={
            <ProtectedRoute requireRole="student">
              <LessonSelectPage />
            </ProtectedRoute>
          } />
          <Route path="/practice/:unit/:lesson" element={
            <ProtectedRoute requireRole="student">
              <PracticePage />
            </ProtectedRoute>
          } />
          <Route path="/mastery-gate/:unit" element={
            <ProtectedRoute requireRole="student">
              <MasteryGatePage />
            </ProtectedRoute>
          } />
          <Route path="/challenge" element={
            <ProtectedRoute requireRole="student">
              <TimedChallengePage />
            </ProtectedRoute>
          } />
          <Route path="/settings" element={
            <ProtectedRoute requireRole="student">
              <StudentSettingsPage />
            </ProtectedRoute>
          } />
          <Route path="/add-grade" element={
            <ProtectedRoute requireRole="student">
              <AddGradePage />
            </ProtectedRoute>
          } />

          {/* Admin route */}
          <Route path="/admin" element={
            <ProtectedRoute requireAdmin>
              <AdminDashboardPage />
            </ProtectedRoute>
          } />

          {/* Protected parent routes */}
          <Route path="/parent/dashboard" element={
            <ProtectedRoute requireRole="parent">
              <ParentDashboardPage />
            </ProtectedRoute>
          } />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </main>
    </div>
  );
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

export default App;
